import { useState, useEffect, useCallback } from 'react';
import api, { formatoMXN } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { ShoppingBag, Plus, Trash2, Package, CreditCard, Edit2 } from 'lucide-react';
import { toast } from 'sonner';

export default function CarwashMostrador() {
  const [productos, setProductos] = useState([]);
  const [ventas, setVentas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [tab, setTab] = useState('vender');

  // Producto dialog
  const [prodDialog, setProdDialog] = useState(false);
  const [editProd, setEditProd] = useState(null);
  const [prodForm, setProdForm] = useState({ nombre: '', descripcion: '', precio: '', stock: '', visible_mostrador: true });
  const [guardandoProd, setGuardandoProd] = useState(false);

  // Venta state
  const [carrito, setCarrito] = useState([]);
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [clienteNombre, setClienteNombre] = useState('');

  const cargar = useCallback(async () => {
    try {
      const [pRes, vRes] = await Promise.all([
        api.get('/carwash/productos'),
        api.get('/carwash/ventas')
      ]);
      setProductos(pRes.data);
      setVentas(vRes.data);
    } catch { /* silent */ }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // ─── Productos CRUD ───
  const abrirNuevoProd = () => { setEditProd(null); setProdForm({ nombre: '', descripcion: '', precio: '', stock: '', visible_mostrador: true }); setProdDialog(true); };
  const abrirEditarProd = (p) => { setEditProd(p); setProdForm({ nombre: p.nombre, descripcion: p.descripcion || '', precio: String(p.precio), stock: String(p.stock), visible_mostrador: p.visible_mostrador !== false }); setProdDialog(true); };

  const guardarProd = async () => {
    if (!prodForm.nombre) { toast.error('El nombre es obligatorio'); return; }
    if (!prodForm.precio || parseFloat(prodForm.precio) <= 0) { toast.error('El precio debe ser mayor a 0'); return; }
    setGuardandoProd(true);
    try {
      if (editProd) {
        await api.put(`/carwash/productos/${editProd.id}`, prodForm);
        toast.success('Producto actualizado');
      } else {
        await api.post('/carwash/productos', prodForm);
        toast.success('Producto creado');
      }
      setProdDialog(false);
      cargar();
    } catch { toast.error('Error al guardar'); }
    finally { setGuardandoProd(false); }
  };

  const eliminarProd = async (id) => {
    if (!window.confirm('Eliminar este producto?')) return;
    try { await api.delete(`/carwash/productos/${id}`); toast.success('Eliminado'); cargar(); }
    catch { toast.error('Error'); }
  };

  // ─── Carrito / Venta ───
  const agregarAlCarrito = (prod) => {
    setCarrito(prev => {
      const existe = prev.find(c => c.producto_id === prod.id);
      if (existe) return prev.map(c => c.producto_id === prod.id ? { ...c, cantidad: c.cantidad + 1 } : c);
      return [...prev, { producto_id: prod.id, nombre: prod.nombre, precio: prod.precio, cantidad: 1 }];
    });
  };

  const actualizarCantidad = (prodId, cant) => {
    if (cant <= 0) { setCarrito(prev => prev.filter(c => c.producto_id !== prodId)); return; }
    setCarrito(prev => prev.map(c => c.producto_id === prodId ? { ...c, cantidad: cant } : c));
  };

  const totalCarrito = carrito.reduce((s, c) => s + c.precio * c.cantidad, 0);

  const cobrarVenta = async () => {
    if (carrito.length === 0) { toast.error('Agrega productos al carrito'); return; }
    try {
      await api.post('/carwash/ventas', {
        items: carrito,
        metodo_pago: metodoPago,
        cliente_nombre: clienteNombre,
      });
      toast.success('Venta registrada');
      setCarrito([]);
      setClienteNombre('');
      setMetodoPago('efectivo');
      cargar();
    } catch { toast.error('Error al registrar venta'); }
  };

  const totalVentasHoy = ventas.reduce((s, v) => s + v.total, 0);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="carwash-mostrador-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50 flex items-center gap-3">
          <ShoppingBag className="w-8 h-8 text-cyan-400" /> Venta de Mostrador
        </h1>
        <div className="flex gap-2">
          {['vender', 'productos', 'historial'].map(t => (
            <Button key={t} variant={tab === t ? 'default' : 'ghost'} size="sm"
              onClick={() => setTab(t)}
              className={tab === t ? 'bg-cyan-500 text-black font-bold' : 'text-slate-400'}
              data-testid={`tab-${t}`}>
              {t === 'vender' ? 'Vender' : t === 'productos' ? 'Productos' : 'Historial'}
            </Button>
          ))}
        </div>
      </div>

      {/* ─── TAB: VENDER ─── */}
      {tab === 'vender' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Productos disponibles */}
          <div className="lg:col-span-2">
            <Card className="bg-slate-900/40 border-slate-800">
              <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Productos Disponibles</CardTitle></CardHeader>
              <CardContent>
                {productos.filter(p => p.activo !== false).length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">No hay productos. Ve a la pestana "Productos" para agregar.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {productos.filter(p => p.activo !== false).map(p => (
                      <button key={p.id} onClick={() => agregarAlCarrito(p)}
                        className="p-3 bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 hover:border-cyan-500/50 rounded-sm text-left transition-all"
                        data-testid={`prod-btn-${p.id}`}>
                        <p className="text-sm text-slate-200 font-medium truncate">{p.nombre}</p>
                        <p className="text-lg text-cyan-400 font-mono font-bold">{formatoMXN(p.precio)}</p>
                        <p className="text-[10px] text-slate-500">Stock: {p.stock}</p>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Carrito */}
          <div>
            <Card className="bg-slate-900/40 border-slate-800">
              <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Carrito</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {carrito.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-6">Selecciona productos</p>
                ) : (
                  <>
                    {carrito.map(c => (
                      <div key={c.producto_id} className="flex items-center justify-between gap-2 p-2 bg-slate-800/40 rounded-sm">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-200 truncate">{c.nombre}</p>
                          <p className="text-xs text-slate-500">{formatoMXN(c.precio)} c/u</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400" onClick={() => actualizarCantidad(c.producto_id, c.cantidad - 1)}>-</Button>
                          <span className="text-sm text-slate-200 w-6 text-center font-mono">{c.cantidad}</span>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400" onClick={() => actualizarCantidad(c.producto_id, c.cantidad + 1)}>+</Button>
                        </div>
                        <span className="text-sm text-cyan-400 font-mono font-bold w-20 text-right">{formatoMXN(c.precio * c.cantidad)}</span>
                      </div>
                    ))}
                    <div className="border-t border-slate-800 pt-3 space-y-3">
                      <div className="flex justify-between text-lg font-bold">
                        <span className="text-slate-200">Total</span>
                        <span className="text-cyan-400 font-mono">{formatoMXN(totalCarrito)}</span>
                      </div>
                      <div><Label className="text-xs uppercase tracking-wider text-slate-500">Metodo de Pago</Label>
                        <Select value={metodoPago} onValueChange={setMetodoPago}>
                          <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="venta-metodo-select"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-800">
                            <SelectItem value="efectivo" className="text-slate-300">Efectivo</SelectItem>
                            <SelectItem value="tarjeta" className="text-slate-300">Tarjeta</SelectItem>
                            <SelectItem value="transferencia" className="text-slate-300">Transferencia</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs uppercase tracking-wider text-slate-500">Cliente (opcional)</Label>
                        <Input value={clienteNombre} onChange={e => setClienteNombre(e.target.value)} placeholder="Nombre" className="bg-slate-950 border-slate-700 text-slate-200" /></div>
                      <Button onClick={cobrarVenta} className="w-full bg-green-500 hover:bg-green-600 text-black font-bold h-11" data-testid="cobrar-venta-btn">
                        <CreditCard className="w-4 h-4 mr-2" /> Cobrar {formatoMXN(totalCarrito)}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ─── TAB: PRODUCTOS ─── */}
      {tab === 'productos' && (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-barlow text-lg text-slate-200">Catalogo de Productos</CardTitle>
              <Button onClick={abrirNuevoProd} className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm" data-testid="nuevo-producto-btn">
                <Plus className="w-4 h-4 mr-2" /> Nuevo Producto
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {productos.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                <p className="text-slate-500">No hay productos registrados</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Producto</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Precio</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-center">Stock</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-center">Estado</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {productos.map(p => (
                    <TableRow key={p.id} className="border-slate-800/50" data-testid={`producto-row-${p.id}`}>
                      <TableCell>
                        <p className="text-sm text-slate-200 font-medium">{p.nombre}</p>
                        {p.descripcion && <p className="text-xs text-slate-500">{p.descripcion}</p>}
                      </TableCell>
                      <TableCell className="text-right text-sm text-cyan-400 font-mono font-bold">{formatoMXN(p.precio)}</TableCell>
                      <TableCell className="text-center">
                        <span className={`text-sm font-mono ${p.stock <= 5 ? 'text-amber-400' : 'text-slate-300'}`}>{p.stock}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={p.activo !== false ? 'bg-green-500/20 text-green-400 border-green-800 text-[10px]' : 'bg-red-500/20 text-red-400 border-red-800 text-[10px]'}>
                          {p.activo !== false ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => abrirEditarProd(p)} className="text-slate-400 hover:text-cyan-400 h-8 w-8" data-testid={`editar-prod-${p.id}`}>
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => eliminarProd(p.id)} className="text-slate-400 hover:text-red-400 h-8 w-8" data-testid={`eliminar-prod-${p.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── TAB: HISTORIAL ─── */}
      {tab === 'historial' && (
        <div className="space-y-4">
          <Card className="bg-slate-900/40 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-400">Ventas del dia: <span className="font-bold text-slate-200">{ventas.length}</span></p>
                <p className="text-lg font-bold text-cyan-400 font-mono">{formatoMXN(totalVentasHoy)}</p>
              </div>
            </CardContent>
          </Card>
          {ventas.length === 0 ? (
            <Card className="bg-slate-900/40 border-slate-800">
              <CardContent className="p-12 text-center">
                <ShoppingBag className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                <p className="text-slate-500">Sin ventas hoy</p>
              </CardContent>
            </Card>
          ) : (
            ventas.map(v => (
              <Card key={v.id} className="bg-slate-900/40 border-slate-800" data-testid={`venta-card-${v.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-slate-300 font-bold">{v.numero_venta}</span>
                      <Badge className="text-[10px] bg-slate-700/50 text-slate-400 border-slate-600">{v.metodo_pago}</Badge>
                      {v.cliente_nombre && <span className="text-xs text-slate-500">{v.cliente_nombre}</span>}
                    </div>
                    <span className="text-lg font-bold text-cyan-400 font-mono">{formatoMXN(v.total)}</span>
                  </div>
                  <div className="space-y-1">
                    {v.items?.map((item, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-slate-500">{item.cantidad}x {item.nombre}</span>
                        <span className="text-slate-400 font-mono">{formatoMXN(item.subtotal)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Dialog: Producto */}
      <Dialog open={prodDialog} onOpenChange={setProdDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="producto-dialog">
          <DialogHeader><DialogTitle className="font-barlow text-xl text-slate-100">{editProd ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Nombre *</Label>
              <Input value={prodForm.nombre} onChange={e => setProdForm({ ...prodForm, nombre: e.target.value })} placeholder="Ej: Aromatizante" className="bg-slate-950 border-slate-700 text-slate-200" data-testid="producto-nombre-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Descripcion</Label>
              <Textarea value={prodForm.descripcion} onChange={e => setProdForm({ ...prodForm, descripcion: e.target.value })} className="bg-slate-950 border-slate-700 text-slate-200" rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Precio (MXN) *</Label>
                <Input type="number" step="0.01" value={prodForm.precio} onChange={e => setProdForm({ ...prodForm, precio: e.target.value })} className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="producto-precio-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Stock</Label>
                <Input type="number" value={prodForm.stock} onChange={e => setProdForm({ ...prodForm, stock: e.target.value })} className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="producto-stock-input" /></div>
            </div>
            <label className="flex items-start gap-3 p-3 rounded-sm border border-slate-800 bg-slate-950/40 cursor-pointer hover:bg-slate-900" data-testid="visible-mostrador-toggle">
              <input type="checkbox" checked={prodForm.visible_mostrador}
                onChange={e => setProdForm({ ...prodForm, visible_mostrador: e.target.checked })}
                className="w-5 h-5 mt-0.5 accent-cyan-500" />
              <div className="flex-1">
                <p className="text-sm text-slate-200 font-medium">Visible al cobrar turno (Mostrador)</p>
                <p className="text-xs text-slate-500">Si esta activo, este producto aparece en la pantalla de cobro para agregarse a un ticket. Desactivalo para productos que solo se venden en el mostrador.</p>
              </div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setProdDialog(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={guardarProd} disabled={guardandoProd} className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold" data-testid="guardar-producto-btn">
              {guardandoProd ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
